import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  FileText, Plus, Search, Filter, RefreshCw, IndianRupee, Calendar,
  CheckCircle, XCircle, ArrowRightCircle, Clock, Users, Building2,
  ChevronRight, AlertTriangle, Undo2, Hash, Download, Upload, Link2, AlertCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import Layout from "../components/Layout";
import DynamicFormRenderer from "../components/DynamicFormRenderer";
import { API } from "../App";
import { toast } from "sonner";

const formatINR = (v) => {
  if (!v) return "N/A";
  const n = Number(v);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
};

const STATUS_STYLES = {
  active: "bg-emerald-100 text-emerald-700",
  converted: "bg-blue-100 text-blue-700",
  refunded: "bg-amber-100 text-amber-700",
  cancelled: "bg-red-100 text-red-700",
};

const EOIPage = () => {
  const [tab, setTab] = useState("all");
  const [eois, setEois] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Create EOI dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    lead_id: "",
  });
  const [leadSearch, setLeadSearch] = useState("");
  const [leadResults, setLeadResults] = useState([]);
  const [searchingLeads, setSearchingLeads] = useState(false);
  const [creating, setCreating] = useState(false);

  // Duplicate EOI warning state
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);

  // Convert EOI dialog
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertEoi, setConvertEoi] = useState(null);
  const [convertForm, setConvertForm] = useState({
    unit_id: "",
    final_price: "",
    additional_payment: "",
    notes: ""
  });
  const [units, setUnits] = useState([]);
  const [converting, setConverting] = useState(false);

  // Refund dialog
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundEoi, setRefundEoi] = useState(null);
  const [refundForm, setRefundForm] = useState({
    amount: "",
    reason: "",
    reference_number: "",
    notes: ""
  });
  const [refunding, setRefunding] = useState(false);

  // Bulk update state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch data
  const fetchEOIs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedProject) params.append("project_id", selectedProject);
      if (tab !== "all") params.append("status", tab);
      
      const [eoisRes, statsRes] = await Promise.all([
        axios.get(`${API}/eoi?${params}`),
        axios.get(`${API}/eoi/stats${selectedProject ? `?project_id=${selectedProject}` : ""}`)
      ]);
      setEois(eoisRes.data);
      setStats(statsRes.data);
    } catch (e) {
      toast.error("Failed to load EOIs");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchEOIs();
  }, [tab, selectedProject]);

  // Lead search
  const searchLeads = async (query) => {
    if (!query || query.length < 2) {
      setLeadResults([]);
      return;
    }
    setSearchingLeads(true);
    try {
      const res = await axios.get(`${API}/leads/search?q=${encodeURIComponent(query)}&limit=10`);
      setLeadResults(res.data);
    } catch (e) { /* ignore */ }
    setSearchingLeads(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => searchLeads(leadSearch), 300);
    return () => clearTimeout(timer);
  }, [leadSearch]);

  // Fetch units for conversion
  const fetchUnits = async (projectId) => {
    try {
      const res = await axios.get(`${API}/inventory?project_id=${projectId}&status=available`);
      setUnits(res.data.units || res.data || []);
    } catch (e) { /* ignore */ }
  };

  // Create EOI
  const handleCreateEOI = async (forceCreate = false) => {
    if (!createForm.lead_id || !createForm.project_id || !createForm.token_amount || !createForm.agreed_price) {
      toast.error("Please fill required fields (Lead, Project, Token Amount, Agreed Price)");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        lead_id: createForm.lead_id,
        project_id: createForm.project_id,
        token_amount: parseFloat(createForm.token_amount),
        agreed_price: parseFloat(createForm.agreed_price),
        payment_mode: createForm.payment_mode,
        payment_reference: createForm.payment_reference || null,
        unit_preference: {
          unit_type: createForm.unit_type || null,
          floor_preference: createForm.floor_preference || null,
          facing: createForm.facing || null,
          other_preferences: null
        },
        preference_notes: createForm.preference_notes || null,
        notes: createForm.notes || null,
        force_create: forceCreate
      };
      const res = await axios.post(`${API}/eoi`, payload);
      
      // Check for duplicate warning
      if (res.data.duplicate_warning && !forceCreate) {
        setDuplicateWarning(res.data);
        setShowDuplicateDialog(true);
        setCreating(false);
        return;
      }
      
      toast.success(res.data.message || "EOI created successfully");
      setCreateOpen(false);
      setCreateForm({
        lead_id: "", project_id: "", token_amount: "", agreed_price: "",
        payment_mode: "NEFT", payment_reference: "", unit_type: "",
        floor_preference: "", facing: "", preference_notes: "", notes: ""
      });
      setLeadSearch("");
      setLeadResults([]);
      fetchEOIs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create EOI");
    }
    setCreating(false);
  };

  // Handle duplicate EOI confirmation
  const handleDuplicateConfirm = () => {
    setShowDuplicateDialog(false);
    handleCreateEOI(true); // Force create
  };

  const handleDuplicateCancel = () => {
    setShowDuplicateDialog(false);
    setDuplicateWarning(null);
  };

  // Convert EOI to Booking
  const handleConvert = async () => {
    if (!convertForm.unit_id || !convertForm.final_price) {
      toast.error("Please select unit and enter final price");
      return;
    }
    setConverting(true);
    try {
      const payload = {
        unit_id: convertForm.unit_id,
        final_price: parseFloat(convertForm.final_price),
        additional_payment: convertForm.additional_payment ? parseFloat(convertForm.additional_payment) : null,
        notes: convertForm.notes || null
      };
      await axios.post(`${API}/eoi/${convertEoi.eoi_id}/convert`, payload);
      toast.success("EOI converted to booking successfully");
      setConvertOpen(false);
      setConvertEoi(null);
      setConvertForm({ unit_id: "", final_price: "", additional_payment: "", notes: "" });
      fetchEOIs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to convert EOI");
    }
    setConverting(false);
  };

  // Process Refund
  const handleRefund = async () => {
    if (!refundForm.amount || !refundForm.reason) {
      toast.error("Please enter refund amount and reason");
      return;
    }
    setRefunding(true);
    try {
      const payload = {
        amount: parseFloat(refundForm.amount),
        reason: refundForm.reason,
        reference_number: refundForm.reference_number || null,
        notes: refundForm.notes || null
      };
      await axios.post(`${API}/eoi/${refundEoi.eoi_id}/refund`, payload);
      toast.success("Refund initiated successfully");
      setRefundOpen(false);
      setRefundEoi(null);
      setRefundForm({ amount: "", reason: "", reference_number: "", notes: "" });
      fetchEOIs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to process refund");
    }
    setRefunding(false);
  };

  // Open convert dialog
  const openConvertDialog = (eoi) => {
    setConvertEoi(eoi);
    setConvertForm({
      unit_id: "",
      final_price: eoi.agreed_price || "",
      additional_payment: "",
      notes: ""
    });
    fetchUnits(eoi.project_id);
    setConvertOpen(true);
  };

  // Open refund dialog
  const openRefundDialog = (eoi) => {
    setRefundEoi(eoi);
    setRefundForm({
      amount: eoi.token_amount,
      reason: "",
      reference_number: "",
      notes: ""
    });
    setRefundOpen(true);
  };

  // Export EOIs to CSV
  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedProject) params.append("project_id", selectedProject);
      if (tab !== "all") params.append("status", tab);
      
      const response = await axios.get(`${API}/eoi/export/csv?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `eoi_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("EOIs exported successfully");
    } catch (e) {
      toast.error("Failed to export EOIs");
    }
  };

  // Handle bulk upload
  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error("Please upload a CSV file");
      return;
    }
    
    setUploading(true);
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/eoi/bulk-update`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadResult(res.data);
      
      if (res.data.updated > 0) {
        toast.success(`${res.data.updated} EOIs updated successfully`);
        fetchEOIs();
      }
      
      if (res.data.total_errors > 0) {
        toast.error(`${res.data.total_errors} errors occurred`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to process file");
      setUploadResult({ success: false, error: e.response?.data?.detail || "Upload failed" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Filter EOIs by search
  const filteredEois = eois.filter(eoi => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      eoi.lead_name?.toLowerCase().includes(q) ||
      eoi.lead_phone?.includes(q) ||
      eoi.project_name?.toLowerCase().includes(q) ||
      eoi.eoi_id?.toLowerCase().includes(q)
    );
  });

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">EOI Management</h1>
            <p className="text-sm text-slate-500 mt-1">Expression of Interest - Pre-launch token collection</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={handleExportCSV} className="text-slate-600">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
            <Button variant="outline" onClick={() => setBulkOpen(true)} className="text-slate-600">
              <Upload className="w-4 h-4 mr-2" /> Bulk Update
            </Button>
            <Button onClick={() => setCreateOpen(true)} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" /> Create EOI
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Total EOIs</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.total_eois}</p>
                    <p className="text-xs text-slate-500">{formatINR(stats.total_amount)}</p>
                  </div>
                  <FileText className="w-8 h-8 text-slate-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-emerald-600 uppercase tracking-wide">Active</p>
                    <p className="text-2xl font-bold text-emerald-700">{stats.active?.count || 0}</p>
                    <p className="text-xs text-emerald-600">{formatINR(stats.active?.amount)}</p>
                  </div>
                  <Clock className="w-8 h-8 text-emerald-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-blue-600 uppercase tracking-wide">Converted</p>
                    <p className="text-2xl font-bold text-blue-700">{stats.converted?.count || 0}</p>
                    <p className="text-xs text-blue-600">{formatINR(stats.converted?.amount)}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-blue-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-amber-600 uppercase tracking-wide">Refunded</p>
                    <p className="text-2xl font-bold text-amber-700">{stats.refunded?.count || 0}</p>
                    <p className="text-xs text-amber-600">{formatINR(stats.refunded?.amount)}</p>
                  </div>
                  <Undo2 className="w-8 h-8 text-amber-300" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, phone, project..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={selectedProject} onValueChange={(v) => setSelectedProject(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="All Projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map(p => (
                <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchEOIs}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full h-auto flex overflow-x-auto gap-1 p-1">
            <TabsTrigger value="all" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">All EOIs</TabsTrigger>
            <TabsTrigger value="active" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">Active</TabsTrigger>
            <TabsTrigger value="converted" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">Converted</TabsTrigger>
            <TabsTrigger value="refunded" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">Refunded</TabsTrigger>
            <TabsTrigger value="cancelled" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">Cancelled</TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="mt-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : filteredEois.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No EOIs found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredEois.map(eoi => (
                  <Card key={eoi.eoi_id} className="bg-white hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        {/* Priority Badge */}
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                            <span className="text-sm font-bold text-slate-600">#{eoi.priority_number}</span>
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{eoi.lead_name}</p>
                            <p className="text-xs text-slate-500">{eoi.lead_phone}</p>
                          </div>
                        </div>

                        {/* Project & Preferences */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 text-sm">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-700">{eoi.project_name}</span>
                          </div>
                          {eoi.unit_preference && (
                            <p className="text-xs text-slate-500 mt-1">
                              {[
                                eoi.unit_preference.unit_type,
                                eoi.unit_preference.floor_preference && `${eoi.unit_preference.floor_preference} floor`,
                                eoi.unit_preference.facing && `${eoi.unit_preference.facing} facing`
                              ].filter(Boolean).join(" • ") || eoi.preference_notes || "No specific preference"}
                            </p>
                          )}
                        </div>

                        {/* Amount & Date */}
                        <div className="text-right">
                          <p className="font-semibold text-slate-900">{formatINR(eoi.agreed_price)}</p>
                          <p className="text-xs text-emerald-600">Token: {formatINR(eoi.token_amount)}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(eoi.eoi_date).toLocaleDateString()}
                          </p>
                        </div>

                        {/* Status & Actions */}
                        <div className="flex items-center gap-2">
                          <Badge className={STATUS_STYLES[eoi.status]}>
                            {eoi.status}
                          </Badge>
                          
                          {eoi.status === "active" && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openConvertDialog(eoi)}
                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              >
                                <ArrowRightCircle className="w-4 h-4 mr-1" />
                                Convert
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openRefundDialog(eoi)}
                                className="text-amber-600 border-amber-200 hover:bg-amber-50"
                              >
                                <Undo2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                          
                          {eoi.status === "converted" && eoi.conversion && (
                            <span className="text-xs text-blue-600">
                              → Booking
                            </span>
                          )}
                          
                          {eoi.status === "refunded" && eoi.refund && (
                            <Badge variant="outline" className="text-xs">
                              {eoi.refund.status}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Create EOI Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create EOI (Expression of Interest)</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Lead Search */}
              <div>
                <label className="text-sm font-medium text-slate-700">Lead *</label>
                <div className="relative mt-1">
                  <Input
                    placeholder="Search by name, phone, email..."
                    value={leadSearch}
                    onChange={(e) => setLeadSearch(e.target.value)}
                  />
                  {leadResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {leadResults.map(lead => (
                        <div
                          key={lead.lead_id}
                          className="px-3 py-2 hover:bg-slate-50 cursor-pointer"
                          onClick={() => {
                            setCreateForm(prev => ({ ...prev, lead_id: lead.lead_id }));
                            setLeadSearch(`${lead.name} (${lead.phone})`);
                            setLeadResults([]);
                          }}
                        >
                          <p className="font-medium text-sm">{lead.name}</p>
                          <p className="text-xs text-slate-500">{lead.phone} • {lead.email}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Dynamic EOI Fields */}
              <DynamicFormRenderer
                formType="eoi_internal"
                values={createForm}
                onChange={(field, value) => setCreateForm(prev => ({ ...prev, [field]: value }))}
                projects={projects}
                showGroups={true}
                gridCols={2}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateEOI} disabled={creating}>
                {creating ? "Creating..." : "Create EOI"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Convert to Booking Dialog */}
        <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Convert EOI to Booking</DialogTitle>
            </DialogHeader>
            {convertEoi && (
              <div className="space-y-4 py-4">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="font-medium">{convertEoi.lead_name}</p>
                  <p className="text-sm text-slate-500">{convertEoi.project_name}</p>
                  <div className="flex justify-between mt-2 text-sm">
                    <span className="text-slate-500">Agreed Price:</span>
                    <span className="font-semibold text-slate-900">{formatINR(convertEoi.agreed_price)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Token Paid:</span>
                    <span className="text-emerald-600">{formatINR(convertEoi.token_amount)}</span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">Select Unit *</label>
                  <Select
                    value={convertForm.unit_id}
                    onValueChange={(v) => setConvertForm({ ...convertForm, unit_id: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Choose available unit" />
                    </SelectTrigger>
                    <SelectContent>
                      {units.map(u => (
                        <SelectItem key={u.unit_id} value={u.unit_id}>
                          {u.unit_number} - {u.unit_type} ({u.floor_number}F) - {formatINR(u.base_price)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">Final Price *</label>
                  <Input
                    type="number"
                    value={convertForm.final_price}
                    onChange={(e) => setConvertForm({ ...convertForm, final_price: e.target.value })}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">Should match agreed price unless adjustments apply</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">Additional Payment</label>
                  <Input
                    type="number"
                    placeholder="Amount paid at conversion"
                    value={convertForm.additional_payment}
                    onChange={(e) => setConvertForm({ ...convertForm, additional_payment: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <Textarea
                  placeholder="Notes (optional)"
                  value={convertForm.notes}
                  onChange={(e) => setConvertForm({ ...convertForm, notes: e.target.value })}
                  rows={2}
                />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setConvertOpen(false)}>Cancel</Button>
              <Button onClick={handleConvert} disabled={converting} className="bg-blue-600 hover:bg-blue-700">
                {converting ? "Converting..." : "Convert to Booking"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Refund Dialog */}
        <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Process Refund</DialogTitle>
            </DialogHeader>
            {refundEoi && (
              <div className="space-y-4 py-4">
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <p className="font-medium text-amber-800">{refundEoi.lead_name}</p>
                  <p className="text-sm text-amber-700">{refundEoi.project_name}</p>
                  <p className="text-sm text-amber-700">Token Amount: {formatINR(refundEoi.token_amount)}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">Refund Amount *</label>
                  <Input
                    type="number"
                    value={refundForm.amount}
                    onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">Reason *</label>
                  <Select
                    value={refundForm.reason}
                    onValueChange={(v) => setRefundForm({ ...refundForm, reason: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Customer requested - Not interested">Not interested anymore</SelectItem>
                      <SelectItem value="Customer requested - Budget issues">Budget issues</SelectItem>
                      <SelectItem value="Customer requested - Found alternative">Found alternative</SelectItem>
                      <SelectItem value="Project cancelled">Project cancelled</SelectItem>
                      <SelectItem value="Price revision not acceptable">Price revision not acceptable</SelectItem>
                      <SelectItem value="Unit not available">Preferred unit not available</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">Reference Number</label>
                  <Input
                    placeholder="Refund transaction ref"
                    value={refundForm.reference_number}
                    onChange={(e) => setRefundForm({ ...refundForm, reference_number: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <Textarea
                  placeholder="Additional notes"
                  value={refundForm.notes}
                  onChange={(e) => setRefundForm({ ...refundForm, notes: e.target.value })}
                  rows={2}
                />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setRefundOpen(false)}>Cancel</Button>
              <Button onClick={handleRefund} disabled={refunding} className="bg-amber-600 hover:bg-amber-700">
                {refunding ? "Processing..." : "Initiate Refund"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Update Dialog */}
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Bulk Update EOIs</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-800 mb-2">How to bulk update:</h4>
                <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Click "Export CSV" to download current EOIs</li>
                  <li>Open in Excel and edit the values</li>
                  <li>Save as CSV and upload below</li>
                </ol>
                <p className="text-xs text-blue-600 mt-2">
                  Note: Only active EOIs can be updated. Do not change the eoi_id column.
                </p>
              </div>

              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".csv"
                  onChange={handleBulkUpload}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-10 h-10 text-slate-400 mb-2" />
                  <span className="text-sm font-medium text-slate-700">
                    {uploading ? "Uploading..." : "Click to upload CSV file"}
                  </span>
                  <span className="text-xs text-slate-500 mt-1">
                    CSV files only
                  </span>
                </label>
              </div>

              {/* Upload Result */}
              {uploadResult && (
                <div className={`p-4 rounded-lg ${uploadResult.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                  {uploadResult.success ? (
                    <>
                      <p className="font-medium text-emerald-800">
                        <CheckCircle className="w-4 h-4 inline mr-2" />
                        {uploadResult.updated} EOIs updated successfully
                      </p>
                      {uploadResult.total_errors > 0 && (
                        <div className="mt-2">
                          <p className="text-sm text-amber-700 font-medium">
                            {uploadResult.total_errors} errors:
                          </p>
                          <ul className="text-xs text-amber-600 mt-1 max-h-32 overflow-y-auto">
                            {uploadResult.errors?.map((err, i) => (
                              <li key={i}>{err}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-red-700">
                      <XCircle className="w-4 h-4 inline mr-2" />
                      {uploadResult.error || "Upload failed"}
                    </p>
                  )}
                </div>
              )}

              <div className="text-sm text-slate-500">
                <strong>Editable fields:</strong> token_amount, agreed_price, payment_mode, 
                payment_reference, unit_type, floor_preference, facing, preference_notes, notes
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setBulkOpen(false); setUploadResult(null); }}>
                Close
              </Button>
              <Button onClick={handleExportCSV} variant="outline">
                <Download className="w-4 h-4 mr-2" /> Export Current EOIs
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Duplicate EOI Warning Dialog */}
        <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertCircle className="w-5 h-5" />
                Existing EOI Found
              </DialogTitle>
              <DialogDescription>
                {duplicateWarning?.message}
              </DialogDescription>
            </DialogHeader>
            
            {duplicateWarning?.existing_eois && (
              <div className="space-y-2 py-4">
                <p className="text-xs text-slate-500 uppercase tracking-wider">
                  Existing EOIs for {duplicateWarning.customer_name}
                </p>
                {duplicateWarning.existing_eois.map((eoi) => (
                  <div key={eoi.eoi_id} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-900">{eoi.project_name || 'Project'}</span>
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">Active</Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                      <span>Token: {formatINR(eoi.token_amount)}</span>
                      {eoi.created_at && (
                        <span>{new Date(eoi.created_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                ))}
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                  <div className="flex items-start gap-2">
                    <Link2 className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-amber-800">
                      If you proceed, the new EOI will be linked to the existing EOI(s) for tracking purposes.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleDuplicateCancel}
                data-testid="eoi-duplicate-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDuplicateConfirm}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="eoi-duplicate-confirm"
              >
                Create Anyway & Link
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default EOIPage;
